import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from "@tanstack/react-query";

import { SentryFeatureTags, logException } from "@punchup/sentry";

type TixologiProps = {
  id?: string;
  isComedian?: boolean;
};

/** What the login route answers for one scope, as `useTixologi` caches it. */
// TODO: Add proper types here
export type TixologiLoginData = {
  token: any;
  partnerId: any;
  error: any;
  // Statsig green-zone rollout: true → host loads the refactored "V2" tixologi
  // build for this user; false/undefined → the existing build. See login route.
  // Optional: absent on older payloads / mocks → treated as false by consumers.
  greenZone?: boolean;
  // Native show surface (per-user Statsig gate `native_show_surface`): true → this
  // user's show pages may leave the iframe for the native routes. Only one input of
  // the host's useShowSurface, which ANDs it with greenZone and the view preference.
  // Optional: absent on older payloads / mocks → treated as false by consumers.
  nativeShowSurface?: boolean;
  // Reporting-engine fork (per-VENUE Statsig gate `reporting_v1_engine`): true →
  // this venue's reports read the v1 revenue engine; false/undefined → legacy.
  // Always false for a comedian-scoped iframe. Unlike greenZone there is no
  // user-preference AND — the gate value passes through as-is. See login route.
  reportingV1?: boolean;
  // The signed-in user's email — the login route returns it for the host's
  // loaded-view event (TixologiEvents.LOADED_UI_MODE).
  email?: string;
};

type GetTixologiDataReturn = TixologiLoginData | undefined;

/** The first element of every login's query key: the prefix that reaches all scopes' logins. */
export const TIXOLOGI_LOGIN_KEY = "tixologiData";

/**
 * The query key `useTixologi` caches one scope's login under: `id` is the venue page or
 * comedian id. Code that seeds or reads that cache builds the key here, never by hand.
 * An absent `isComedian` is a venue login, so it shares the `false` key.
 */
export const tixologiLoginKey = (id?: string, isComedian?: boolean) =>
  [TIXOLOGI_LOGIN_KEY, id, isComedian === true] as const;

/** One scope's login: the query function `useTixologi` caches under `tixologiLoginKey`. */
export const getTixologiData = async (
  id?: string,
  isComedian = false
): Promise<GetTixologiDataReturn | undefined> => {
  if (!id) return;

  try {
    const url = `/api/tixologi/${id}/login`;
    const response = await fetch(url + `?isComedian=${isComedian}`);
    const data = await response.json();

    if (data.error) {
      return {
        token: null,
        partnerId: null,
        error: data.error,
        greenZone: false,
        nativeShowSurface: false,
        reportingV1: false,
      };
    }
    return {
      token: data.token,
      partnerId: data.partner_id,
      error: null,
      greenZone: data.green_zone ?? false,
      nativeShowSurface: data.native_show_surface ?? false,
      reportingV1: data.reporting_v1 ?? false,
      email: data.email ?? undefined,
    };
  } catch (error) {
    logException(error, {
      tags: { feature: SentryFeatureTags.VenueManager },
      extra: {
        data: "getTixologiData",
        [isComedian ? "comedianId" : "venuePageId"]: id,
      },
    });

    return {
      token: null,
      partnerId: null,
      error: "Error connecting to Tixologi",
      greenZone: false,
      nativeShowSurface: false,
      reportingV1: false,
    };
  }
};

interface UseTixologiReturn {
  tixologiData: GetTixologiDataReturn;
  /** Logs in again. Pass `{ cancelRefetch: false }` where another caller may already have a login in flight, to share it. */
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GetTixologiDataReturn>>;
  /** When the current login landed (0 before the first). */
  dataUpdatedAt: number;
}

export function useTixologi({
  id,
  isComedian,
}: TixologiProps): UseTixologiReturn {
  const {
    data: tixologiData,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: tixologiLoginKey(id, isComedian),
    queryFn: () => getTixologiData(id, isComedian || false),
    staleTime: 1000 * 60 * 50, // 50 minutes
    enabled: !!id,
  });

  return { tixologiData, refetch, dataUpdatedAt };
}
